import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/services/api';
import { migrateLegacySession } from '@/lib/legacySession';
import { AUTH_STORAGE_KEY } from '@/lib/appScope';

migrateLegacySession();

/**
 * Session state. POST /api/auth/login returns a bearer credential in
 * `api_key` plus the identity fields around it — role, display name,
 * tenant_id, and, for drivers, driver_id.
 *
 * Since P1-13 an administrator's credential is a PERSONAL session token
 * (core/admin_sessions.py), not the company's shared api_key: the server
 * has to know which of two dispatchers is acting, or the audit trail can
 * only ever say "admin". The field name is unchanged because it is still
 * exactly the same thing to this store — a bearer credential — and
 * renaming it would have meant touching the SSE query param and every
 * request header for nothing.
 *
 * A browser already signed into the monolith is carried over rather than
 * bounced to the login screen — see lib/legacySession.js, which folds the
 * demo's five flat localStorage keys into the one blob persist expects.
 *
 * Note the key lives in localStorage, which is what the demo did and what
 * the SSE endpoint's ?api_key= query param assumes. Moving to an
 * httpOnly-cookie session is a backend change (and a real hardening win),
 * tracked separately — this refactor deliberately does not smuggle an auth
 * redesign into a component-extraction phase.
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      apiKey: null,
      role: null,
      driverId: null,
      tenantId: null,
      displayName: '',
      // WHICH administrator this session belongs to. The login response has
      // carried both since P1-13 and this store threw them away, which was
      // fine while nothing in the console could see other people — V-61's
      // user list can, and a list of logins that cannot mark which row is
      // YOURS is a list somebody deactivates themselves from. The server
      // refuses that, but a refusal arriving after the click is a worse
      // answer than a button that was never offered.
      userId: null,
      username: '',
      // The dispatcher's phone number, kept from the last successful fetch.
      // Persisted because the one screen that needs it most is the one where
      // it can no longer be fetched: when a tenant is suspended, the route
      // that serves this number is refused along with everything else, so a
      // driver reopening the app would be told to call their dispatcher with
      // no number to call. See useDispatcherContact().
      dispatcherPhone: null,

      signIn: (body) =>
        set({
          apiKey: body.api_key,
          role: body.role,
          displayName: body.name || '',
          driverId: body.driver_id || null,
          // Only present on a driver login; a dispatcher discovers its
          // tenant via GET /api/tenants instead (see useTenant()).
          tenantId: body.tenant_id || null,
          // Null on a driver login and on a session restored from the
          // legacy monolith blob — both are real states, and the user list
          // simply marks nothing as "you" rather than guessing.
          userId: body.user_id || null,
          username: body.username || '',
        }),

      /** Called both by the user and by a 401 from any request.
       *
       * Tells the server too (P1-13). Signing in now mints a personal
       * session rather than handing back the company's shared api_key, so
       * there is something to end — and on a borrowed laptop, ending it is
       * the entire point. Fire-and-forget on purpose: the local state is
       * cleared either way, because a sign-out that fails because the
       * network is down must still sign the person out of the screen in
       * front of them. */
      signOut: ({ notifyServer = true } = {}) => {
        const { apiKey } = get();
        // Not when the sign-out was CAUSED by a 401. The credential is
        // already dead, so telling the server to end it is a second
        // request that can only ever be a no-op — and doing it from inside
        // the 401 handler is how a request failure turns into two.
        if (apiKey && notifyServer) {
          api('/api/auth/logout', { method: 'POST', body: { api_key: apiKey } }).catch(() => {});
        }
        set({
          apiKey: null, role: null, driverId: null, tenantId: null, displayName: '',
          userId: null, username: '',
          dispatcherPhone: null,
        });
      },

      setTenantId: (tenantId) => set({ tenantId }),

      setDispatcherPhone: (dispatcherPhone) => set({ dispatcherPhone }),
    }),
    {
      // Per-app key: the console and the Driver App share an origin, and a
      // single key had them overwriting each other's session — see
      // lib/appScope.js.
      name: AUTH_STORAGE_KEY,
      partialize: (s) => ({
        apiKey: s.apiKey,
        role: s.role,
        driverId: s.driverId,
        tenantId: s.tenantId,
        displayName: s.displayName,
        userId: s.userId,
        username: s.username,
        dispatcherPhone: s.dispatcherPhone,
      }),
    },
  ),
);

/** Non-reactive read, for the API client and the SSE manager — both live
 *  outside React and must not subscribe to the store. */
export const getAuth = () => useAuthStore.getState();
